/**
 * Admin-configurable pop-ups — the single reader, normalizer and frequency gate.
 *
 * The backend delivers a `popups` array on the /initial-settings payload. Every
 * pop-up is disabled by default in the back office, so a brand that has enabled
 * nothing gets `popups: []` — and every helper here degrades to "no pop-up",
 * which is exactly the currently-deployed behaviour.
 *
 * Contract per entry:
 *   { type, title, body, body_lines[], button_text, button_url, image_url,
 *     trigger: { event, config }, frequency }
 */

import { getInitialSettings } from "utils/initialSettings";

export const POPUP_TYPES = {
  REGISTRATION_ABANDONMENT: "registration_abandonment",
  EXIT_INTENT: "exit_intent",
  POST_LOGIN_BONUS_REMINDER: "post_login_bonus_reminder",
};

export const POPUP_EVENTS = {
  [POPUP_TYPES.REGISTRATION_ABANDONMENT]: "signup_abandoned",
  [POPUP_TYPES.EXIT_INTENT]: "exit_intent",
  [POPUP_TYPES.POST_LOGIN_BONUS_REMINDER]: "post_login_bonus_pending",
};

export const POPUP_FREQUENCIES = {
  ONCE_PER_SESSION: "once_per_session",
  ONCE_PER_DAY: "once_per_day",
  EVERY_TIME: "every_time",
};

const VALID_FREQUENCIES = Object.values(POPUP_FREQUENCIES);

/**
 * `button_url` is `""`, a `/path` or an `https://` URL and nothing else. Anything
 * outside that set (javascript:, data:, a bare word) is dropped to "", which
 * turns the button into a plain close — never a navigation we did not sanction.
 */
function sanitizeButtonUrl(value) {
  const url = String(value == null ? "" : value).trim();
  if (!url) return "";
  if (url.startsWith("//")) return "";
  if (url.startsWith("/")) return url;
  if (/^https:\/\/[^\s]+$/i.test(url)) return url;
  return "";
}

/** Absolute http(s) image URLs only; anything else is treated as "no image". */
function sanitizeImageUrl(value) {
  const url = String(value == null ? "" : value).trim();
  return /^https?:\/\/[^\s]+$/i.test(url) ? url : null;
}

function normalizeBodyLines(raw) {
  const lines = Array.isArray(raw.body_lines) ? raw.body_lines : null;
  const source = lines && lines.length ? lines : String(raw.body || "").split("\n");
  return source.map((l) => String(l == null ? "" : l).trim()).filter(Boolean);
}

/** One raw entry -> a renderable pop-up, or null when it is unusable. */
function normalizePopup(raw) {
  if (!raw || typeof raw !== "object") return null;
  const type = String(raw.type || "").trim();
  if (!type) return null;

  const title = String(raw.title || "").trim();
  const bodyLines = normalizeBodyLines(raw);
  // Nothing to say = nothing to show. Guards against a half-filled admin row
  // rendering an empty box on a live site.
  if (!title && !bodyLines.length) return null;

  const trigger = raw.trigger && typeof raw.trigger === "object" ? raw.trigger : {};
  const config = trigger.config && typeof trigger.config === "object" ? trigger.config : {};
  const frequency = VALID_FREQUENCIES.includes(raw.frequency)
    ? raw.frequency
    : POPUP_FREQUENCIES.ONCE_PER_SESSION;

  return {
    type,
    title,
    bodyLines,
    buttonText: String(raw.button_text || "").trim(),
    buttonUrl: sanitizeButtonUrl(raw.button_url),
    imageUrl: sanitizeImageUrl(raw.image_url),
    event: String(trigger.event || "").trim(),
    triggerConfig: config,
    frequency,
  };
}

/** Every usable pop-up on the payload. Empty for an older backend or a brand with none. */
export function getPopups(initialSettings) {
  const raw = initialSettings && initialSettings.popups;
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizePopup).filter(Boolean);
}

/**
 * The enabled pop-up of a given type, or null. The trigger event must match the
 * contract pair, so a mistyped back-office row can never drive the wrong trigger.
 */
export function getPopup(initialSettings, type) {
  const expectedEvent = POPUP_EVENTS[type];
  return (
    getPopups(initialSettings).find(
      (p) => p.type === type && (!expectedEvent || p.event === expectedEvent)
    ) || null
  );
}

/** Convenience reader that goes through the shared per-page-load settings cache. */
export async function loadPopup(type) {
  const settings = await getInitialSettings();
  return getPopup(settings, type);
}

/** Non-negative integer from a trigger config key, with a fallback. */
export function triggerNumber(popup, key, fallback = 0) {
  const value = Number(popup && popup.triggerConfig ? popup.triggerConfig[key] : NaN);
  return Number.isFinite(value) && value >= 0 ? value : fallback;
}

/* ------------------------------------------------------------------ *
 * Frequency caps — one owner for all three frequencies, keyed by type.
 * ------------------------------------------------------------------ */

const STORAGE_KEY_PREFIX = "gccPopupShown:";

function storageKey(type) {
  return STORAGE_KEY_PREFIX + type;
}

// Storage throws in private-browsing / blocked-cookie contexts. A pop-up whose
// cap cannot be read is not shown, so failure is quiet and conservative.
function readStore(store, key) {
  try {
    return window[store].getItem(key);
  } catch (e) {
    return null;
  }
}

function writeStore(store, key, value) {
  try {
    window[store].setItem(key, value);
  } catch (e) {
    /* ignore */
  }
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * May this pop-up show right now? The ONLY authority on that question — callers
 * must not render a pop-up that fails here.
 */
export function canShowPopup(popup) {
  if (!popup || !popup.type) return false;
  switch (popup.frequency) {
    case POPUP_FREQUENCIES.EVERY_TIME:
      return true;
    case POPUP_FREQUENCIES.ONCE_PER_DAY:
      return readStore("localStorage", storageKey(popup.type)) !== today();
    case POPUP_FREQUENCIES.ONCE_PER_SESSION:
    default:
      return readStore("sessionStorage", storageKey(popup.type)) !== "1";
  }
}

/** Record that it was shown, so the cap applies from here on. */
export function markPopupShown(popup) {
  if (!popup || !popup.type) return;
  if (popup.frequency === POPUP_FREQUENCIES.ONCE_PER_DAY) {
    writeStore("localStorage", storageKey(popup.type), today());
    return;
  }
  if (popup.frequency === POPUP_FREQUENCIES.ONCE_PER_SESSION) {
    writeStore("sessionStorage", storageKey(popup.type), "1");
  }
}

/* ------------------------------------------------------------------ *
 * Request bus — lets non-React trigger code ask the host to show one.
 * Mirrors the existing "profile-gate-required" window-event pattern.
 * ------------------------------------------------------------------ */

export const POPUP_REQUEST_EVENT = "gcc-popup-request";

export function requestPopup(type) {
  try {
    window.dispatchEvent(new CustomEvent(POPUP_REQUEST_EVENT, { detail: { type } }));
  } catch (e) {
    /* ignore */
  }
}
